import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SlideOver from "../../Modal/SlideOver";
import FormDate from "../../Form/FormDate";
import FormInput from "../../Form/FormInput";
import {
  addLocalNotification,
  clearAccountingTransaction,
  clearAcctTransactionError,
  requestUpdateAcctTransaction,
  useAccountingDispatch,
  useAccountingState,
  useAuthState,
  useNotificationDispatch,
  usePeriodLock,
} from "../../../../Context";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import PeriodClosedNotice from "../PeriodClosedNotice";
import { formatDateTime, getPeriodClosedError } from "../../../../Utils";
import { TRANSACTION_TYPES } from "../../../../Constants";
import InvoiceDetailModal from "../InvoiceDetailModal";
import { ArrowSmRightIcon, ArrowSmUpIcon } from "@heroicons/react/outline";
import { NumericFormat } from "react-number-format";
import FormError from "../../Form/FormError";

const getTypeInfo = (type) => {
  const found = TRANSACTION_TYPES.find((t) => t.value === type);
  return { label: found ? found.label : type };
};

const InvoicePaymentModal = ({
  open,
  onClose,
  payment,
  parentInvoice: parentInvoiceProp,
}) => {
  const { t, i18n } = useTranslation();

  const dispatch = useAccountingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const {
    submitLoading,
    submitError,
    transactionID,
    parentTransactionLoading,
  } = useAccountingState();

  const [isInvoiceDetailOpen, setIsInvoiceDetailOpen] = useState(false);
  const [date, setDate] = useState(new Date());
  const [total, setTotal] = useState("");
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const parentInvoice = parentInvoiceProp || null;

  // The payment record names its own business; mainBusiness is only the fallback
  // for the moment before `payment` arrives.
  const {
    minDate,
    isDateClosed,
    isClosed,
    closedThroughLabel,
    refetch: refetchPeriod,
  } = usePeriodLock(payment?.businessID || user?.mainBusiness?.id, {
    locale: i18n.language,
  });
  const dateLocked = isDateClosed(date);

  // Prefill form when payment changes
  useEffect(() => {
    if (payment) {
      setDate(payment.createdAt ? new Date(payment.createdAt) : new Date());
      setTotal(
        payment.total
          ? { floatValue: payment.total, value: payment.total }
          : "",
      );
      setDescription(payment.description || "");
      setErrors({});
      setSubmitted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.id]);

  // Live re-validate while submitted
  useEffect(() => {
    if (submitted) {
      setErrors({
        date: date ? "" : t("invoices.payment.errors.dateRequired"),
        total:
          (total?.floatValue || 0) > 0
            ? ""
            : t("invoices.payment.errors.totalRequired"),
      });
    }
  }, [date, total, submitted, t]);

  // Handle save success
  useEffect(() => {
    if (transactionID && open && !isInvoiceDetailOpen) {
      clearAccountingTransaction(dispatch);
      addLocalNotification(notificationDispatch, {
        id: `update-payment-${transactionID}`,
        type: "success",
        title: "common.success",
        body: "transactions.notifications.updateSuccess",
        showFull: true,
      });
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionID]);

  const validate = () => {
    const newErrors = {
      date: date ? "" : t("invoices.payment.errors.dateRequired"),
      total:
        (total?.floatValue || 0) > 0
          ? ""
          : t("invoices.payment.errors.totalRequired"),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSave = () => {
    if (writeBlocked || dateLocked) return;
    setSubmitted(true);
    if (!validate() || !payment) return;

    const input = {
      transaction_id: payment.id,
      business_id: payment.businessID,
      type: payment.type,
      paid: true,
      created_at: date.toISOString(),
      description: description,
      total: total.floatValue,
      partner_id: payment.partnerID || null,
      parent_transaction_id: payment.parentTransactionID,
      accounts: (payment.accounts?.items || []).map((a) => ({
        id: a.accountID,
        debit: a.debit > 0 ? total.floatValue : 0,
        credit: a.credit > 0 ? total.floatValue : 0,
      })),
    };

    requestUpdateAcctTransaction(dispatch, { input });
  };

  const handleClose = () => {
    clearAcctTransactionError(dispatch);
    setIsInvoiceDetailOpen(false);
    setErrors({});
    setSubmitted(false);
    onClose();
  };

  const parentTypeInfo = parentInvoice ? getTypeInfo(parentInvoice.type) : null;

  return (
    <>
      <SlideOver
        open={open && !isInvoiceDetailOpen}
        onClose={handleClose}
        title="invoices.payment.editTitle"
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                writeBlocked ||
                dateLocked ||
                submitLoading ||
                parentTransactionLoading
              }
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            >
              {submitLoading ? t("common.loading") : t("common.save")}
            </button>
          </div>
        }
      >
        <div className="space-y-6 mb-6">
          {/* Parent invoice skeleton */}
          {payment?.parentTransactionID && parentTransactionLoading && (
            <div className="bg-white border border-gray-200 shadow-sm overflow-hidden rounded-lg animate-pulse">
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-gray-200 rounded-lg w-9 h-9" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 bg-gray-200 rounded w-1/3" />
                    <div className="h-3 bg-gray-200 rounded w-1/4" />
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <div className="h-3.5 bg-gray-200 rounded w-20" />
                    <div className="h-3 bg-gray-200 rounded w-16" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {parentInvoice && parentTypeInfo && !parentTransactionLoading && (
            <button
              type="button"
              onClick={() => setIsInvoiceDetailOpen(true)}
              className="w-full text-left bg-white border border-gray-200 divide-y divide-gray-200 shadow-sm overflow-hidden rounded-lg cursor-pointer hover:border-blue-300 transition-colors duration-150"
            >
              <div className="px-4 py-4 sm:px-6">
                <div className="flex">
                  <div className="flex flex-1 gap-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-amber-100 border border-amber-300 rounded-lg">
                        {["PAYABLE", "EXPENSE_PAYABLE"].includes(
                          parentInvoice.type,
                        ) ? (
                          <ArrowSmRightIcon className="w-5 h-5 text-amber-600 rotate-45" />
                        ) : (
                          <ArrowSmUpIcon className="w-5 h-5 text-amber-600 rotate-45" />
                        )}
                      </div>
                    </div>
                    <div className="flex items-start flex-col">
                      <p className="text-sm font-medium text-gray-900">
                        {parentTypeInfo.label}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(parentInvoice.createdAt, i18n.language)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <NumericFormat
                      className="text-sm font-medium text-gray-900"
                      displayType="text"
                      value={parentInvoice.total}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="Rp"
                    />
                    {parentInvoice.invoiceNumber && (
                      <p className="text-xs text-gray-500">
                        Invoice #{parentInvoice.invoiceNumber}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </button>
          )}

          {/* The parent-invoice preview above stays readable; only the input
              card is replaced when the business is write-blocked. */}
          <div className="bg-white shadow sm:rounded-lg border border-gray-100">
            <WriteBlockGate>
            <div className="flex flex-col gap-4 px-4 py-5 sm:px-6">
              <PeriodClosedNotice
                error={submitError}
                onRefresh={refetchPeriod}
              />
              {!getPeriodClosedError(submitError) && (
                <FormError error={submitError} />
              )}

              <FormInput
                required
                label="invoices.payment.fields.totalPaid"
                placeholder={t("invoices.payment.fields.totalPaidPlaceholder")}
                value={total?.value ?? ""}
                numberFormat={{
                  thousandSeparator: ".",
                  decimalSeparator: ",",
                  prefix: "Rp",
                  onValueChange: (val) => setTotal(val),
                }}
                error={errors.total}
              />

              <div>
                <FormDate
                  required
                  label="invoices.payment.fields.date"
                  placeholder={t("invoices.payment.fields.datePlaceholder")}
                  selected={date}
                  onChange={setDate}
                  dateFormat="dd/MM/yyyy HH:mm"
                  showTimeInput
                  timeInputLabel={t("common.time")}
                  minDate={minDate}
                  isDateClosed={isDateClosed}
                  error={errors.date}
                />
                {isClosed && (
                  <p className="mt-1.5 text-xs text-amber-700">
                    {dateLocked
                      ? t("accounting.period.hints.dateLocked", {
                          date: closedThroughLabel,
                        })
                      : t("accounting.period.banner.closedThrough", {
                          date: closedThroughLabel,
                        })}
                  </p>
                )}
              </div>

              <FormInput
                label="invoices.payment.fields.note"
                value={description}
                placeholder={t("invoices.payment.fields.notePlaceholder")}
                onChange={(e) => setDescription(e.target.value)}
                onClear={() => setDescription("")}
                hint={t("common.optional")}
              />
            </div>
            </WriteBlockGate>
          </div>
        </div>
      </SlideOver>

      {/* Invoice Detail */}
      {isInvoiceDetailOpen && (
        <InvoiceDetailModal
          open={isInvoiceDetailOpen}
          invoice={parentInvoice}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default InvoicePaymentModal;
